import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import ProductCard from "../components/ProductCard";
import Placeholder from "../components/Placeholder";
import { api, formatEUR } from "../lib/api";
import { useStore } from "../context/StoreContext";

const ProductDetail = () => {
  const { slug } = useParams();
  const { addToCart, settings } = useStore();
  const [product, setProduct] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [qty, setQty] = useState(1);
  const [customText, setCustomText] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    setProduct(null); setNotFound(false); setSize(""); setColor(""); setQty(1); setCustomText("");
    api.get(`/products/${slug}`)
      .then((p) => {
        setProduct(p);
        const firstAvail = p.sizes.find((s) => s.stock > 0);
        if (firstAvail) setSize(firstAvail.size);
        if (p.colors.length) setColor(p.colors[0]);
      })
      .catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <main className="pt-40 pb-32 text-center px-4 min-h-screen" data-testid="product-not-found">
        <p className="font-display font-extrabold uppercase text-outline text-6xl">Introuvable</p>
        <Link to="/boutique" data-testid="not-found-back-button" className="mt-8 inline-block bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] px-10 py-4 hover:bg-gold transition-colors">
          Retour à la boutique
        </Link>
      </main>
    );
  }

  if (!product) {
    return <main className="pt-40 pb-32 text-center font-mono text-xs uppercase tracking-[0.3em] text-neutral-500 min-h-screen" data-testid="product-loading">Chargement…</main>;
  }

  const stockFor = (s) => product.sizes.find((x) => x.size === s)?.stock ?? 0;
  const selectedStock = size ? stockFor(size) : 0;
  const related = null;

  const handleAdd = () => {
    if (!size) { setError("Choisissez une taille."); return; }
    if (selectedStock < qty) { setError("Stock insuffisant."); return; }
    setError("");
    addToCart(product, size, color, qty, customText);
  };

  return (
    <main className="pt-28 sm:pt-36 pb-24 px-4 sm:px-8 lg:px-16 min-h-screen" data-testid="product-page">
      <nav className="font-mono text-[11px] uppercase tracking-[0.2em] text-neutral-500" aria-label="Fil d'ariane">
        <Link to="/boutique" className="hover:text-white transition-colors">Boutique</Link>
        <span className="mx-2">/</span>
        <span className="text-neutral-300">{product.name}</span>
      </nav>

      <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
        {/* ----- Visuel ----- */}
        <Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="product-visual">
            {(product.images && product.images.length ? product.images : [null]).map((src, i, arr) => (
              <div key={i} className="relative aspect-[4/5] border border-line bg-panel overflow-hidden" data-testid={`product-visual-${i}`}>
                {src ? (
                  <img src={src} alt={`${product.name} — vue ${i + 1}`} className="w-full h-full object-cover" />
                ) : (
                  <Placeholder label={product.name} />
                )}
                {arr.length > 1 && (
                  <span className="absolute top-3 left-3 bg-ink/80 backdrop-blur px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-white border border-white/20" data-testid={`product-view-label-${i}`}>
                    {i === 0 ? "Devant" : i === 1 ? "Dos" : `Vue ${i + 1}`}
                  </span>
                )}
              </div>
            ))}
          </div>
        </Reveal>

        {/* ----- Infos ----- */}
        <div className="flex flex-col">
          <Reveal delay={0.05}>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">{product.category}</p>
            <h1 className="mt-3 font-display font-extrabold uppercase tracking-tight text-3xl sm:text-5xl text-white" data-testid="product-name">{product.name}</h1>
            <p className="mt-4 font-mono text-2xl text-silver" data-testid="product-price">{formatEUR(product.price)}</p>
          </Reveal>

          <Reveal delay={0.1}>
            <p className="mt-6 text-sm sm:text-base text-neutral-400 leading-relaxed" data-testid="product-description">{product.description}</p>
          </Reveal>

          {product.colors.length > 0 && (
            <Reveal delay={0.12}>
              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">Coloris — <span className="text-white">{color}</span></p>
              <div className="mt-3 flex gap-2">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    data-testid={`product-color-${c.toLowerCase().replace(/\s/g, "-")}`}
                    onClick={() => setColor(c)}
                    className={`px-4 py-2 font-mono text-xs uppercase tracking-[0.12em] border transition-colors ${color === c ? "bg-white text-ink border-white" : "border-line text-neutral-400 hover:text-white hover:border-lineActive"}`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </Reveal>
          )}

          {product.customizable && (
            <Reveal delay={0.13}>
              <p className="mt-8 font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">
                Personnalisable — numéro sur la capuche <span className="text-gold">(2 chiffres max)</span>
              </p>
              <input
                data-testid="product-custom-input"
                inputMode="numeric"
                placeholder="Ex : 66"
                maxLength={product.custom_max_len || 2}
                value={customText}
                onChange={(e) => setCustomText(e.target.value.replace(/\D/g, "").slice(0, product.custom_max_len || 2))}
                className="mt-3 w-36 bg-panel border border-line focus:border-white outline-none px-4 py-3 text-center font-mono text-lg tracking-[0.2em] text-white placeholder:text-neutral-600 transition-colors"
              />
            </Reveal>
          )}

          <Reveal delay={0.14}>
            <div className="mt-8 flex items-center justify-between">
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-neutral-500">Taille</p>
              {size && (
                <p className="font-mono text-[11px] uppercase tracking-[0.2em]" data-testid="product-stock-indicator">
                  {selectedStock > 5 ? (
                    <span className="text-neutral-400">En stock</span>
                  ) : selectedStock > 0 ? (
                    <span className="text-gold">Plus que {selectedStock} en stock</span>
                  ) : (
                    <span className="text-neutral-600">Épuisé</span>
                  )}
                </p>
              )}
            </div>
            <div className="mt-3 grid grid-cols-4 gap-2" data-testid="product-size-selector">
              {[...product.sizes]
                .sort((a, b) => ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"].indexOf(a.size) - ["XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"].indexOf(b.size))
                .map((sObj) => {
                const s = sObj.size;
                const stock = stockFor(s);
                const disabled = stock === 0;
                return (
                  <button
                    key={s}
                    data-testid={`product-size-${s.toLowerCase()}`}
                    disabled={disabled}
                    onClick={() => { setSize(s); setError(""); }}
                    className={`relative h-12 font-mono text-sm border transition-all duration-200
                      ${disabled ? "border-line text-neutral-700 cursor-not-allowed line-through"
                        : size === s ? "bg-white text-ink border-white font-bold"
                        : "border-lineActive text-white hover:bg-white/10"}`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </Reveal>

          <Reveal delay={0.16}>
            <div className="mt-8 flex items-stretch gap-3">
              <div className="flex items-center border border-line">
                <button data-testid="product-qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-11 h-full text-neutral-400 hover:text-white">−</button>
                <span data-testid="product-qty-value" className="w-10 text-center font-mono text-sm">{qty}</span>
                <button data-testid="product-qty-plus" onClick={() => setQty((q) => Math.min(q + 1, selectedStock || 1))} className="w-11 h-full text-neutral-400 hover:text-white">+</button>
              </div>
              <button
                data-testid="add-to-cart-button"
                onClick={handleAdd}
                disabled={!size || selectedStock === 0}
                className="flex-1 bg-white text-ink font-mono text-xs uppercase tracking-[0.25em] py-4 hover:bg-gold disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-300"
              >
                {selectedStock === 0 ? "Épuisé" : "Ajouter au panier — " + formatEUR(product.price * qty)}
              </button>
            </div>
            {error && <p className="mt-3 font-mono text-xs text-red-400" data-testid="product-error">{error}</p>}
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-10 border-t border-line" data-testid="product-accordion">
              {[
                { t: "Livraison & retours", d: `Expédition sous 24-48h ouvrées. Livraison offerte dès ${settings?.shipping?.free_threshold ? formatEUR(settings.shipping.free_threshold) : "50 €"} d'achat. Retours acceptés sous 14 jours — voir les pages Livraison et Retours.` },
                { t: "Entretien", d: "Lavage à 30°C sur l'envers, séchage à plat, pas de sèche-linge pour préserver les impressions et broderies." },
                { t: "Guide des tailles", d: "Coupes oversize : pour une fit ajustée, prenez votre taille habituelle ; pour un tombé ample, prenez une taille au-dessus." },
              ].map((row, i) => (
                <details key={i} className="group border-b border-line" data-testid={`product-detail-accordion-${i}`}>
                  <summary className="flex items-center justify-between py-4 cursor-pointer font-mono text-xs uppercase tracking-[0.2em] text-neutral-300 hover:text-white transition-colors list-none">
                    {row.t}
                    <span className="text-neutral-500 group-open:rotate-45 transition-transform duration-300">+</span>
                  </summary>
                  <p className="pb-4 text-sm text-neutral-400 leading-relaxed">{row.d}</p>
                </details>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </main>
  );
};

export default ProductDetail;
